import { useState } from "react";
import { motion } from "framer-motion";
import { PRODUCT_IMAGES } from "../../lib/constants";
import { Share2, Instagram, MessageCircle, Heart } from "lucide-react";
import { toast } from "sonner";

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryImages = [
    { id: 1, src: PRODUCT_IMAGES.boxesSorted, alt: "Cajas surtidas", likes: 234 },
    { id: 2, src: PRODUCT_IMAGES.cakeBirthday, alt: "Cake de cumpleaños", likes: 189 },
    { id: 3, src: PRODUCT_IMAGES.browniesWithBow, alt: "Brownies con lazo", likes: 156 },
    { id: 4, src: PRODUCT_IMAGES.urbanCup, alt: "Urban Cup", likes: 145 },
    { id: 5, src: PRODUCT_IMAGES.brownieChips, alt: "Brownie con chips", likes: 178 },
    { id: 6, src: PRODUCT_IMAGES.cakeCaramel, alt: "Cake de arequipe", likes: 210 },
  ];

  const shareToWhatsApp = (image) => {
    const message = encodeURIComponent(
      `¡Mira estos brownies increíbles de Urban Brownies! 🍫\n\nPide los tuyos: https://wa.me/573107544509`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
    toast.success("¡Abriendo WhatsApp para compartir!");
  };

  const shareToInstagram = () => {
    window.open("https://www.instagram.com/urbanbrownies1", "_blank");
    toast.info("Visita nuestro Instagram para compartir en Stories");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Enlace copiado al portapapeles");
  };

  return (
    <section
      id="galeria"
      className="py-20 md:py-32 bg-[#F4EDE4]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-[#C88037] font-medium text-sm uppercase tracking-wider">
            Galería
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D1810] mt-2 mb-4">
            Momentos <span className="italic text-[#C88037]">deliciosos</span>
          </h2>
          <p className="text-[#5E4B3C] max-w-xl mx-auto">
            ¿Te antojaste? Comparte tu favorito con alguien especial. 
            ¡Cuidado: puede causar antojos inmediatos!
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {galleryImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer"
              onClick={() => setSelectedImage(image)}
              data-testid={`gallery-image-${image.id}`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-[#2D1810]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-4">
                <div className="flex items-center gap-2 text-white">
                  <Heart className="w-5 h-5 fill-white" strokeWidth={1.5} />
                  <span className="font-semibold">{image.likes}</span>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      shareToWhatsApp(image);
                    }}
                    className="p-3 bg-[#25D366] rounded-full text-white hover:bg-[#20BD5A] transition-colors"
                    data-testid={`share-whatsapp-${image.id}`}
                  >
                    <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      shareToInstagram();
                    }}
                    className="p-3 bg-gradient-to-br from-purple-600 to-pink-500 rounded-full text-white hover:opacity-90 transition-opacity"
                    data-testid={`share-instagram-${image.id}`}
                  >
                    <Instagram className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyLink();
                    }}
                    className="p-3 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
                    data-testid={`share-link-${image.id}`}
                  >
                    <Share2 className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-[#5E4B3C] mb-4">
            Síguenos en Instagram para más contenido delicioso
          </p>
          <a
            href="https://www.instagram.com/urbanbrownies1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-br from-purple-600 to-pink-500 text-white px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            data-testid="follow-instagram"
          >
            <Instagram className="w-5 h-5" strokeWidth={1.5} />
            @urbanbrownies1
          </a>
        </motion.div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="gallery-lightbox"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="relative max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="w-full rounded-2xl"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 p-2 bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors"
            >
              <span className="text-xl">×</span>
            </button>
            <div className="absolute bottom-4 left-4 right-4 flex justify-center gap-4">
              <button
                onClick={() => shareToWhatsApp(selectedImage)}
                className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full font-semibold"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
                Compartir
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default GallerySection;
